import { Component, Input, OnInit, ViewChild } from '@angular/core';
import { ModalController, AlertController } from '@ionic/angular';
import { NgForm } from '@angular/forms';

import { Observable } from 'rxjs';

import { DocumentReference, ServiceYearMember } from 'src/core/models';
import {
  AgeGroupService, ArrayService, AuthService, ChurchService, DatabaseService, 
  DocumentService, EmailService, ServiceYearMemberService, SportTeamService, FestivalUserService
} from 'src/core/services';
import { Gender, Sport } from 'src/core/enums';
import { PageClass } from 'src/core/classes/page';

@Component({
  selector: 'app-edit-team',
  templateUrl: './edit-team.component.html',
  styleUrls: ['./edit-team.component.less', '../../core/styles/common.less']
})

export class EditTeamComponent extends PageClass implements OnInit {

  @Input() sportTeam: any;

  createdAt: string;

  serviceYearMembers: DocumentReference[];
  allServiceYearMembers: ServiceYearMember[];
  serviceYearMembers$: Observable<ServiceYearMember[]>;
  areMembersAccepted: boolean = true;

  approverList: DocumentReference[] = [];
  currentChurch: any;
  currentLeague: any;
  leagueRefList: DocumentReference [];
  sportName: string;

  rules: string;
  submitText: string;

  @ViewChild('editTeamForm', null) editTeamForm: NgForm;

  constructor(
    protected ageGroupService: AgeGroupService,
    protected arrayService: ArrayService,
    protected authService: AuthService,
    protected churchService: ChurchService,
    protected databaseService: DatabaseService,
    private documentService: DocumentService,
    protected emailService: EmailService,
    private serviceYearMemberService: ServiceYearMemberService,
    protected sportTeamService: SportTeamService,
    protected festivalUserService: FestivalUserService,
    private modalController: ModalController,
    public alertController: AlertController,
  ) { 
    super(
      ageGroupService, arrayService, authService, 
      churchService, databaseService, festivalUserService
    );
  }

  protected loadData = async () => {
    if (this.currentUser.isActive && !this.currentUser.isDeleted) {
      this.submitText = this.currentUser.isChurchApprover ? 'Approve' : 'Submit';
      this.getSportTeam();
    } else {
      this.dismissModal();
    }
  }

  private async getSportTeam() {
      const createdDate = new Date(this.sportTeam.createdAt);
      const date = createdDate.getDate();
      const month = createdDate.getMonth();
      const year = createdDate.getFullYear();
      this.createdAt  = `${date} ${this.monthNames[month]} ${year}`;
      this.sportName = Sport[this.sportTeam.sport];

      var uniqueMembers = [];
      this.sportTeam.memberList.forEach(m => {
        if (!uniqueMembers.find(um => um.reference.id === m.reference.id)) {
          uniqueMembers.push(m);
        }
      })
      
      this.sportTeam.memberList = uniqueMembers;

      this.getLists();
  }

  private async getLists() {
    this.currentChurch = await this.documentService.getDocumentById(this.churchService.collection, this.sportTeam.church.reference.id);

    this.currentLeague = await this.documentService.getDocumentById('sportLeagues', this.sportTeam.league.reference.id);

    this.populateRulesAndMembers();

    (await this.festivalUserService.getApprovers(this.sportTeam.church))
      .subscribe(approverList => {
        approverList.forEach(approver => {
            this.approverList.push(
                {
                    reference: this.databaseService.doc(`${this.festivalUserService.collection}/${approver.id}`).ref,
                    displayName: approver.user.displayName
                }
            );
        });
        this.sportTeam.approver = this.approverList.find( a => a.reference.id === this.sportTeam.approver.reference.id);
      });
  }

  private populateRulesAndMembers() {

    this.rules = `<li> Church can have max of ${this.currentLeague.maxTeams} team(s).</li>`;
    this.rules += `<li> Team must be ${this.currentLeague.minMembers} and ${this.currentLeague.maxMembers} player(s).</li>`;
    if (this.currentLeague.minFemale > 0) {
      this.rules += `<li> Team must include ${this.currentLeague.minFemale} female player(s).</li>`;
    }
    if (this.currentLeague.maxMale > 0) {
      this.rules += `<li> Team cannot include more than ${this.currentLeague.maxMale} male player(s).</li>`;
    }

    this.getMemberGroupList();
  }

  private getMemberGroupList() {
    if (!this.sportTeam.church || !this.sportTeam.sport || !this.sportTeam.league) {
      return;
    }

    this.serviceYearMembers = [];

    const leagueGender = this.currentLeague.gender;

    if (!!leagueGender) {
      this.serviceYearMembers$ = this.serviceYearMemberService.getTeamMembersYearLevelsByGender(
        this.sportTeam.church, this.currentLeague.yearLevelList, leagueGender);
    } else {
      this.serviceYearMembers$ = this.serviceYearMemberService.getTeamMembersYearLevels(
        this.sportTeam.church, this.currentLeague.yearLevelList);
    }

    this.serviceYearMembers$.subscribe(serviceYearMembers => {
      this.serviceYearMembers = [];
      this.allServiceYearMembers = serviceYearMembers;

      serviceYearMembers.map(a => this.serviceYearMembers.push({
        displayName: `${a.member.displayName} - ${a.yearLevel.displayName} - ${a.gender.name}`, 
        reference: this.databaseService.doc(`${this.serviceYearMemberService.collection}/${a.id}`).ref
      }));

      this.serviceYearMembers.sort(this.arrayService.orderByDisplayName);

      // Get SportTeam MemberList from serviceYearMembers
      const teamMembers = this.sportTeam.memberList;
      var newTeamMembers = [];

      teamMembers.forEach(member => {
        newTeamMembers.push(this.serviceYearMembers.filter(m => m.reference.id == member.reference.id)[0]);
      });

      this.sportTeam.memberList = newTeamMembers;

      this.isLoading = false;
    });
  }

  async editSportTeam(status: boolean) {
    if (this.editTeamForm.invalid) {
      return;
    }

    if (status && this.sportTeam.isApproved  && this.currentUser.isChurchApprover) {
      this.sportTeam.status = 'Approved';
    } else if (status) {
      this.sportTeam.status = 'Submitted';
      this.sportTeam.isApproved = false;
    } else {
      this.sportTeam.status = 'Saved';
      this.sportTeam.isApproved = false;
    }

    this.sportTeam.updatedBy = this.currentUser.user;
    this.sportTeam.updatedAt = new Date().getTime();

    this.sportTeam.yearLevelList = this.currentLeague.yearLevelList;

    await this.documentService.updateDocument('sportTeams', this.sportTeam);

    await this.emailService.sendSportTeamEmail(this.sportTeam);

    this.dismissModal();
  }

  dismissModal() {
    this.modalController.dismiss();
  }

  public canSubmit(): boolean {

    if (this.currentUser.isChurchApprover && !this.sportTeam.isApproved) {
      return false;
    }

    if (!this.areMembersAccepted)
      return false

    if ( !this.sportTeam.memberList ||
         this.sportTeam.memberList.length < this.currentLeague.minMembers ||
         this.sportTeam.memberList.length > this.currentLeague.maxMembers ) {
        return false;
    }

    if ( this.currentLeague.isMixedGender) {
      let femaleMemberCount = 0;
      let maleMemberCount = 0;
      
      this.sportTeam.memberList.map( a => {
        // tslint:disable-next-line: ban-types
        const memberGender = this.allServiceYearMembers.find( m => m.id === a.reference.id).gender.value;
        if (memberGender === Gender.Female) {
          femaleMemberCount++;
        } else {
          maleMemberCount++;
        }
      });

      if (femaleMemberCount < this.currentLeague.minFemale || maleMemberCount > this.currentLeague.maxMale) {
        return false;
      }
    }

    return true;
  }

  public memberListUpdated = () => {
    if (this.sportTeam.memberList.length === 1) {
      var memberName = this.allServiceYearMembers.find( m => m.id === this.sportTeam.memberList[0].reference.id).member.displayName;
      this.sportTeam.displayName = memberName;
    }
  
    this.areMembersAccepted = false;
    
    this.sportTeamService.getSportLeagueTeams(this.sportTeam.church, this.sportTeam.sport, this.sportTeam.league)
      .subscribe((leagueTeams) => {

        if (leagueTeams?.length > 0) {
          
          for (var leagueTeam of leagueTeams) {
            if (leagueTeam.id === this.sportTeam.id)
              continue;
              
            var members = leagueTeam.memberList;

            for (var pMember of this.sportTeam.memberList) {

              if (members.find(m => m.reference.id == pMember.reference.id)) {
                this.showErrorMessage(
                  'Cannot Add Member', 
                  `Sorry, you cannot add member ${pMember.displayName}, member is already in another team`);
                return;
              }
            };         
          };
        }

        this.areMembersAccepted = true;
      })
  }

  showErrorMessage = async (header: string, errorMessage: string) => {
    const alert = await this.alertController.create({
      header: header,
      message: errorMessage,
      buttons: ['OK'],
    });

    // alert.onDidDismiss().then(() => { 
    //   if (clearData)
    //     this.clearData();
    // });

    await alert.present();
  }

  isApprovedTeam = (): boolean => {
    return this.sportTeam.status === 'Approved' && !this.currentUser.isChurchApprover;
  }

  presentRules = async () => {
    const rules = await this.alertController.create({
      header: 'Sport Team Rules',
      message: this.rules,
      buttons: ['OK'],
    });

    await rules.present();
  }
}
